import React, { useMemo, useState } from 'react';
import {
  Box, Button, ButtonBase, Chip, CircularProgress, Collapse, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, Divider, Stack, Typography,
} from '@mui/material';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import { useInventoryPending } from '../hooks/useInventoryPending';
import { CACHE_WARNING_RECORDS } from '../utils/constants';

/**
 * Displays this device's operation status across the active organization's inventories.
 */
export default function OfflineIndicator({ networkState, isOffline }) {
  const { t } = useTranslation();
  const offline = useMemo(
    () => (typeof networkState === 'string' ? networkState === 'offline' : !!isOffline),
    [networkState, isOffline],
  );
  const {
    pendingRecords, errors, errorsCount, errorMessage, retry, clear,
  } = useInventoryPending();
  const [showErrors, setShowErrors] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const showSyncing = pendingRecords > 0 && errorsCount === 0;
  const showCacheWarning = pendingRecords >= CACHE_WARNING_RECORDS;
  const showErrorStatus = errorsCount > 0;

  if (!showSyncing && !showErrorStatus && !offline) return null;

  return (
    <>
      {showSyncing && (
        <ButtonBase
          component="button"
          type="button"
          data-testid="pending-sync-indicator"
          aria-live="polite"
          aria-label={t('operation.sync_info_open')}
          onClick={() => setShowInfo(true)}
          sx={{
            position: 'fixed',
            top: '72px',
            left: 0,
            zIndex: (theme) => theme.zIndex.appBar + 1,
            display: 'flex',
            alignItems: 'center',
            maxWidth: { xs: 'calc(100vw - 24px)', sm: '33.333vw' },
            px: 1.5,
            py: 0.75,
            borderRadius: '0 999px 999px 0',
            bgcolor: 'warning.main',
            color: 'warning.contrastText',
            boxShadow: 2,
            textAlign: 'left',
          }}
        >
          {showCacheWarning ? (
            <WarningAmberRoundedIcon sx={{ mr: 1, flexShrink: 0, fontSize: '1.1rem' }} />
          ) : (
            <InfoOutlinedIcon sx={{ mr: 1, flexShrink: 0, fontSize: '1.1rem' }} />
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, overflowWrap: 'anywhere' }}>
              {t('operation.records_to_send', { count: pendingRecords })}
            </Typography>
            {showCacheWarning && (
              <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.1 }}>
                {t('operation.sync_connection_prompt')}
              </Typography>
            )}
          </Box>
          <CircularProgress
            size={18}
            thickness={4}
            color="inherit"
            sx={{ ml: 1.5, flexShrink: 0 }}
          />
        </ButtonBase>
      )}

      {(showErrorStatus || offline || showCacheWarning) && (
        <Box sx={{ my: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
              {showErrorStatus && (
              <Chip
                size="small"
                icon={<ErrorOutlineRoundedIcon />}
                label={t('operation.sync_blocked')}
                color="error"
                variant="filled"
                sx={{
                  borderRadius: 999,
                  fontWeight: 600,
                  '& .MuiChip-icon': { color: 'inherit' },
                  boxShadow: 1,
                }}
              />
              )}
              {offline && (
              <Chip size="small" variant="outlined" label={t('operation.offline')} />
              )}
              {showErrorStatus && (
              <Chip
                size="small"
                color="error"
                icon={<ErrorOutlineRoundedIcon />}
                label={t('operation.errors_label', { count: errorsCount })}
                onClick={() => setShowErrors((value) => !value)}
                variant={showErrors ? 'filled' : 'outlined'}
                sx={{
                  borderRadius: 999,
                  fontWeight: 600,
                  boxShadow: showErrors ? 1 : 'none',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': { transform: 'scale(1.05)' },
                }}
              />
              )}
            </Stack>
          </Box>

          {showCacheWarning && (
          <Box sx={{
            mt: 1,
            p: 1.5,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'warning.light',
            bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'warning.900' : 'warning.50'),
          }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <WarningRoundedIcon sx={{ color: 'warning.dark', fontSize: '1.2em' }} />
              <Typography variant="caption" sx={{ display: 'block', textAlign: 'left', color: 'warning.dark' }}>
                {t('operation.offline_save_warning')}
              </Typography>
            </Stack>
          </Box>
          )}

          <Collapse in={showErrors} timeout="auto" unmountOnExit>
            <Box sx={{
              mt: 1, p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'error.light',
            }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'error.main' }}>
                  {t('operation.errors_title')}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button size="small" onClick={() => setShowErrors(false)}>{t('operation.errors_hide')}</Button>
                  <Button size="small" onClick={() => retry?.()}>{t('operation.retry_sync')}</Button>
                  <Button size="small" color="error" onClick={() => clear?.()}>
                    {t('operation.errors_clear')}
                  </Button>
                </Stack>
              </Stack>
              <Divider sx={{ mb: 1 }} />
              <Stack spacing={0.5} sx={{ textAlign: 'left' }}>
                {!!errorMessage && <Typography variant="caption">{errorMessage}</Typography>}
                {errors.map((error) => {
                  let messageKey;
                  if (error.type === 'missing-record') messageKey = 'operation.error_missing_record';
                  else if (error.type === 'record') messageKey = 'operation.error_record_write';
                  else if (error.type === 'update') messageKey = 'operation.error_update_create';
                  else messageKey = 'operation.error_flush_failed';
                  return (
                    <Typography key={error.id} variant="caption" sx={{ display: 'block', textAlign: 'left' }}>
                      •
                      {' '}
                      {error.containerId ? `#${error.containerId}` : ''}
                      {' '}
                      {t(messageKey)}
                      {error.type === 'missing-record' && (
                      <span>{` (${error.inventoryId} / ${error.updateId} / ${error.recordId})`}</span>
                      )}
                    </Typography>
                  );
                })}
              </Stack>
            </Box>
          </Collapse>
        </Box>
      )}

      <Dialog open={showInfo} onClose={() => setShowInfo(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('operation.sync_info_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('operation.sync_info_text')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInfo(false)}>{t('operation.sync_info_close')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
